import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuth, apiErrorMessage, type User } from '~/lib/auth'
import { AppHeader } from '~/components/app-header'
import { RequireAuth } from '~/components/require-auth'
import { Button } from '~/components/ui/button'
import { Notice } from '~/components/ui/notice'

export const Route = createFileRoute('/account')({
  head: () => ({
    meta: [
      { title: 'Account — Provenance' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  component: AccountPage,
})

function AccountPage() {
  return (
    <RequireAuth>
      <AppHeader />
      <AccountBody />
    </RequireAuth>
  )
}

function initials(user: User): string {
  const a = user.firstName?.[0] ?? ''
  const b = user.lastName?.[0] ?? ''
  const combined = (a + b).trim()
  return (combined || user.email[0] || '?').toUpperCase()
}

function fullName(user: User): string {
  return [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
}

function AccountBody() {
  const { user, logout, forgotPassword } = useAuth()
  const navigate = useNavigate()
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  if (!user) return null

  const rows: Array<{ k: string; v: string }> = [
    { k: 'Name', v: fullName(user) || '—' },
    { k: 'Email', v: user.email },
    { k: 'Phone', v: user.phone || '—' },
    { k: 'Email verified', v: user.emailVerified ? 'Yes' : 'No' },
    {
      k: 'Member since',
      v: user.createdAt ? user.createdAt.slice(0, 10).replace(/-/g, '.') : '—',
    },
  ]

  async function handleLogout() {
    await logout()
    navigate({ to: '/login' })
  }

  async function handleChangePassword() {
    setError('')
    setNotice('')
    try {
      await forgotPassword(user!.email)
      setNotice('We sent a password reset code to your email.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not start password reset'))
    }
  }

  return (
    <main className="mx-auto max-w-[760px] px-[var(--gutter)] py-12 sm:py-16">
      <div className="mb-10 flex items-center gap-5">
        <div
          className="flex h-16 w-16 shrink-0 items-center justify-center border border-[var(--line-strong)] bg-surface-2 font-mono text-[22px] font-[500] text-accent"
          aria-hidden="true"
        >
          {initials(user)}
        </div>
        <div>
          <h1 className="font-mono text-[26px] font-[500] leading-[1.1] text-ink">
            {fullName(user) || 'Your account'}
          </h1>
          <p className="mt-1 font-mono text-[11px] tracking-[0.12em] text-ink-mute">
            {user.email}
          </p>
        </div>
      </div>

      <section className="bracket-frame relative border border-[var(--line)] bg-surface">
        <span className="bk-bl" aria-hidden="true" />
        <span className="bk-br" aria-hidden="true" />
        <div className="border-b border-[var(--line)] px-7 py-4 font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
          Profile
        </div>
        <dl className="divide-y divide-[var(--line)]">
          {rows.map((r) => (
            <div
              key={r.k}
              className="grid grid-cols-[160px_1fr] gap-4 px-7 py-4"
            >
              <dt className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink-mute">
                {r.k}
              </dt>
              <dd className="text-[14px] text-ink">{r.v}</dd>
            </div>
          ))}
        </dl>
      </section>

      {(notice || error) && (
        <div className="mt-6">
          {error && <Notice>{error}</Notice>}
          {notice && <Notice tone="success">{notice}</Notice>}
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <Button variant="outline" onClick={handleChangePassword}>
          Change password
        </Button>
        <Button variant="ghost" onClick={handleLogout}>
          Sign out
        </Button>
      </div>
    </main>
  )
}
